/** @file Changes the project fps; rescales every key frame to keep its time in seconds unless keepFrames. */
import { z } from 'zod';
import type { Draft } from 'immer';
import type { AnimationDoc, Warning } from '@pose2d/format/types';
import { defineCommand } from '../defineCommand';
import { currentOf } from '../_shared/currentOf';
import { copyJson } from '../animation/copyJson';

type Keyed = { f: number };
type Group = Record<string, Record<string, Keyed[] | undefined> | undefined>;

/**
 * A rescaled copy of a sorted key list (new objects, so no key draft is made); with `merge`, keys landing on one
 * frame keep only the later one. Null when no frame moves, so the list is not written.
 */
function rescaled<K extends Keyed>(keys: readonly K[], ratio: number, merge: boolean): K[] | null {
  if (keys.every((k) => Math.round(k.f * ratio) === k.f)) return null;
  const out: K[] = [];
  for (const key of copyJson(keys)) {
    key.f = Math.round(key.f * ratio);
    if (merge && out.at(-1)?.f === key.f) out[out.length - 1] = key;
    else out.push(key);
  }
  return out;
}

/** Rescales each key list of a `{ target: { channel: keys } }` group, one write per list; returns how many keys merged. */
function rescaleGroup(read: Group, write: Group, ratio: number): number {
  let dropped = 0;
  for (const [target, track] of Object.entries(read)) {
    for (const [channel, keys] of Object.entries(track ?? {})) {
      if (keys === undefined) continue;
      const out = rescaled(keys, ratio, true);
      if (out === null) continue;
      dropped += keys.length - out.length;
      const into = write[target];
      if (into) into[channel] = out;
    }
  }
  return dropped;
}

/**
 * Rescales one animation, reading the plain snapshot `read` and writing `anim`: one replace patch per changed list,
 * never one per key (R17-01c). Events keep duplicates: several events may share a frame.
 */
function rescaleAnimation(read: AnimationDoc, anim: Draft<AnimationDoc>, ratio: number): number {
  let dropped = 0;
  for (const group of ['bones', 'slots', 'ik'] as const) dropped += rescaleGroup(read[group], anim[group], ratio);
  dropped += rescaleGroup({ deform: read.deform }, { deform: anim.deform }, ratio);
  const order = rescaled(read.drawOrder, ratio, true);
  if (order) {
    dropped += read.drawOrder.length - order.length;
    anim.drawOrder = order;
  }
  const events = rescaled(read.events, ratio, false);
  if (events) anim.events = events;
  if (read.durationFrames !== undefined) anim.durationFrames = Math.max(1, Math.round(read.durationFrames * ratio));
  return dropped;
}

/** The warning for keys merged by rounding in animation `id`. */
function collisionWarning(id: string, name: string, dropped: number): Warning {
  return { code: 'INFO', message: `${dropped} keys of "${name}" landed on the same frame; the later key was kept`, params: { animation: name, dropped }, path: `animations.${id}` };
}

export const command = defineCommand({
  name: 'project.setFps',
  description: 'Set the project fps (1..240); key frames are rescaled to keep their time in seconds unless keepFrames is true.',
  input: z.object({ fps: z.number().int().min(1).max(240), keepFrames: z.boolean().exactOptional() }).strict(),
  apply(draft, input, ctx) {
    const ratio = input.fps / draft.meta.fps;
    draft.meta.fps = input.fps;
    draft.meta.updatedAt = ctx.now;
    if (input.keepFrames === true || ratio === 1) return;
    for (const [id, read] of Object.entries(currentOf(draft.animations))) {
      const anim = draft.animations[id];
      if (!anim) continue;
      const dropped = rescaleAnimation(read, anim, ratio);
      if (dropped > 0) ctx.warnings.push(collisionWarning(id, read.name, dropped));
    }
  },
});
